import { API_VERSION } from "./env";

export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: "/oauth/token",
    LOGOUT: "/oauth/logout",
    REFRESH: "/oauth/token",
    PROFILE: "/auth/profile",
  },
  SIGNUP: {
    REGISTER: `${API_VERSION}/user/register`,
    EMAIL_VERIFICATION: `${API_VERSION}/user/email-verification`,
    PASSWORD_RESET: `${API_VERSION}/user/password-reset`,
    REQUEST_PASSWORD_RESET: `${API_VERSION}/user/request-password-reset`,
  },
  // Dashboard endpoints
  DASHBOARD: {
    GET_ALL: `${API_VERSION}/dashboard`,
  },
  // Calls endpoints
  CALLS: {
    GET_ALL: `${API_VERSION}/calls`,
    GET_BY_ID: (id: string) => `${API_VERSION}/call/${id}`,
    CREATE: "/calls",
    UPDATE: (id: string) => `${API_VERSION}/calls/${id}`,
    DELETE: (id: string) => `${API_VERSION}/calls/${id}`,
    ACTION: `${API_VERSION}/call_action`,
    DECLARED_INTEREST: `${API_VERSION}/declared_interest_calls`,
    BOOKMARKED: `${API_VERSION}/bookmarked_calls`,
    ARCHIVED: `${API_VERSION}/my_archived_calls/`,
    EXPORT_CALL: (call_id: string) => `${API_VERSION}/call/${call_id}/export`,
    EXPORT_CALLS: `${API_VERSION}/calls/export`,
    GET_GENERAL_DATA: `${API_VERSION}/call_general_data`,
  },
  // Partners endpoints
  PARTNERS: {
    GET_ALL: `${API_VERSION}/partners`,
    GET_BY_ID: (id: string) => `${API_VERSION}/partner/${id}`,
    SEARCH: "/partners/search",
    ACTION: `${API_VERSION}/partner_action`,
    ORG_SEARCH: (search: string) =>
      `${API_VERSION}/partners/autocomplete/?search=${search}`,
    INVITATIONS_REQUESTS: `${API_VERSION}/partner_invitations_and_requests`,
    ACTION_ANSWER: `${API_VERSION}/partner_action_answer`,
    CHANGE_ADMIN: `${API_VERSION}/partner_change_admin`, //target_uid
    REMOVE_MEMBER: `${API_VERSION}/partner_remove_user`, //target_uid
    SEND_INVITATIONS: `${API_VERSION}/partner_invite_users`,
    PARTNER_LEAVE: `${API_VERSION}/partner_leave`,
    PARTNER_ORG_INFO_UPDATE: `${API_VERSION}/partner/entity`,
    PARTNER_PROPOSE: `${API_VERSION}/partners/proposed`,
    GET_PARTNERS_GENERAL_DATA: `${API_VERSION}/partner_general_data`,
  },
  // Ideas endpoints
  IDEAS: {
    GET_ALL: `${API_VERSION}/ideas`,
    GET_BY_ID: (id: string) => `${API_VERSION}/idea/${id}`,
    CREATE: `${API_VERSION}/idea/entity`,
    UPDATE: (id: string) => `${API_VERSION}/idea/entity/${id}`,
    IDEA_ACTION: () => `${API_VERSION}/idea_action`,
    IDEA_ACCEPT_DENY: `${API_VERSION}/idea_action_answer`,
    IDEA_STATUS_ACTION: (
      id: string,
      action: "publish" | "unpublish" | "archive" | "restore",
    ) => `${API_VERSION}/idea_status_action/${action}/${id}`,
    IDEA_DELETE: (id: string) => `${API_VERSION}/idea/entity/${id}`,
    IDEA_INVITATIONS_REQUESTS: `${API_VERSION}/ideas_guest_invitations_and_requests`,
    LEAVE: `${API_VERSION}/idea_leave`,
    IDEA_INVITE_PARTNET: `${API_VERSION}/idea_invite_partner`,
    SEND_INVITATIONS: `${API_VERSION}/idea_invite_users`,
    GET_INVITATIONS_OWNER: (idea_id: string) =>
      `${API_VERSION}/idea/${idea_id}/invitations_and_requests`,
    REMOVE_MEMBER: `${API_VERSION}/idea_remove_user`, // Payload {  "idea_id": 123,   "user_id": 123 }
    MAKE_COORDINATOR: `${API_VERSION}/idea_user_coordinator`, // Payload {  "idea_id": 123,   "user_id": 123 }
  },
  // Goals endpoints
  GOALS: {
    GET_ALL: `${API_VERSION}/funding_goals`,
    GET_MATCHING_CALLS: (id: string) =>
      `${API_VERSION}/funding_goal/${id}/calls`,
    GET_BY_ID: (id: string) => `${API_VERSION}/funding_goal/${id}`,
    CREATE: `${API_VERSION}/funding_goal/entity`,
    GET_ENTITY: (id: string) => `${API_VERSION}/funding_goal/entity/${id}`,
    UPDATE: (id: string) => `${API_VERSION}/funding_goal/entity/${id}`,
    DELETE: (id: string) => `${API_VERSION}/funding_goal/entity/${id}`,
    ANALYZE: (id: string) => `${API_VERSION}/funding_goal/${id}/analyze`,
    GET_CALL_GOALS: (call_id: string) =>
      `${API_VERSION}/funding_goals/${call_id}`,
    EXPORT: (id: string) => `${API_VERSION}/funding_goal/${id}/calls/export`,
  },
  USER: {
    GET_PROFILE: `${API_VERSION}/user/my_profile`,
    UPDATE_PROFILE: `${API_VERSION}/user/my_profile`,
    SETTINGS: `${API_VERSION}/user/account-settings`,
    NOTIFICATIONS: `${API_VERSION}/user/my_profile_notifications`,
    BASIC_INFO: `${API_VERSION}/user/user_basic`,
    FOLLOW: `${API_VERSION}/user_follow`,
    MY_NETWORK: `${API_VERSION}/my_network`, // type: 'followers' | 'following'
    USERS: `${API_VERSION}/users`, // type: 'followers' | 'following'
    GET_USER: (id: string) => `${API_VERSION}/user/${id}`,
    PARTNER: `${API_VERSION}/user/partner`,
    VIEWS: `${API_VERSION}/user_views`,
    USER_PICTURE: `${API_VERSION}/file/upload/user/user/user_picture`,
    AUTOCOMPLETE: (search: string) =>
      `${API_VERSION}/users/autocomplete?search=${encodeURIComponent(search)}`,
  },
  GLOBAL: {
    TERM: `${API_VERSION}/terms/{voc_id}`,
  },
  TERMS: {
    GET_EXPENDITURES: `${API_VERSION}/terms/expenditure`,
    GET_COUNTRIES: `${API_VERSION}/terms/countries`,
    GET_EXPERTISE: `${API_VERSION}/terms/expertise`,
    GET_FUNDING_RANGE: `${API_VERSION}/terms/funding_range`,
    GET_FUNDING_TYPE: `${API_VERSION}/terms/funding_type`,
    GET_SECTORS: `${API_VERSION}/terms/sectors`,
  },
  COMMENTS: {
    GET_CALL_COMMENTS: (id: string) => `${API_VERSION}/comments/${id}`,
    CREATE_CALL_COMMENT: `${API_VERSION}/call_comment`,
    GET_IDEA_COMMENTS: (id: string) => `${API_VERSION}/discussion/${id}`,
    CREATE_IDEA_COMMENT: `${API_VERSION}/idea_comment`,
  },
  NOTIFICATIONS: {
    GET_ALL: `${API_VERSION}/notifications`,
    READ: `${API_VERSION}/notification_read`,
  },
  SUBSCRIPTIONS: {
    GET_PLANS: `${API_VERSION}/plans`,
    GET_ACTIVE_SUBSCRIPTION: `${API_VERSION}/active_subscription`,
    GET_STRIPE_LINK: `${API_VERSION}/stripe/checkout`,
    CANCEL: `${API_VERSION}/subscription/cancel`,
  },
  BILLING: {
    BILLING_INFO: `${API_VERSION}/billing-info`,
  },
  FILES: {
    UPLOAD: `${API_VERSION}/file/upload`,
  },
  MERCURE: {
    GET_TOKEN: `${API_VERSION}/get-mercure-token`,
  },
  CHAT: {
    GET_ALL: `${API_VERSION}/conversations`,
    GET_HISTORY: (conversationId: string) =>
      `${API_VERSION}/conversation/${conversationId}`,
    GET_OR_CREATE: (uid: string) =>
      `${API_VERSION}/private_conversation/${uid}`,
    POST_MESSAGE: `${API_VERSION}/post_message`,
    MARK_READ: (conversationId: string) =>
      `${API_VERSION}/conversation_read/${conversationId}`,
    DELETE: (conversationId: string) =>
      `${API_VERSION}/conversation/${conversationId}/delete`,
    BLOCK_USER: `${API_VERSION}/block_user`,
    BLOCK_REASONS: `${API_VERSION}/terms/block_reason`,
    MESSAGE_FILE: (fid: number) => `${API_VERSION}/message_file/${fid}`,
  },
  RECAPTCHA: {
    VALIDATE: `${API_VERSION}/verify-recaptcha`,
  },
};
